/**
 * Attachments Service
 * API client for file upload and attachment operations
 */

import apiClient, { tokenManager } from '@/services/api.client';
import type { ApiResponse, Attachment } from '@/types/api.types';

/**
 * Uploads single or multiple file attachments
 * @param files File or array of files to upload
 * @param messageId Message ID to attach files to
 * @param onProgress Optional progress callback (0-100)
 * @param compress Whether to compress the file (for images/videos)
 * @param displayType How to display: 'image' for inline preview, 'file' for download
 * @returns Attachment metadata (single or array)
 */
export const uploadAttachment = async (
  file: File,
  messageId: string,
  onProgress?: (progress: number) => void,
  compress?: boolean,
  displayType?: 'image' | 'file'
): Promise<Attachment> => {
  const formData = new FormData();
  formData.append('files', file); // Backend expects 'files' field

  // Add compress flag if requested
  if (compress) {
    formData.append('compress', 'true');
  }

  // Add display type (defaults to 'file' on backend if not provided)
  if (displayType) {
    formData.append('displayType', displayType);
  }

  const fileSize = file.size || 1048576; // fallback 1MB for progress estimation

  const response = await apiClient.post<ApiResponse<{ attachments: Attachment[]; count: number }>>(
    `/messages/${messageId}/attachments`,
    formData,
    {
      timeout: 600000, // 10 minutes for large file uploads (up to 500MB)
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          if (progressEvent.total && progressEvent.total > 0) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            onProgress(Math.max(5, progress));
          } else if (progressEvent.loaded > 0) {
            // total unknown — estimate using known file size
            const ratio = Math.min(progressEvent.loaded / fileSize, 0.95);
            onProgress(Math.max(5, Math.round(ratio * 95)));
          }
        }
      },
    }
  );

  if (!response.data.data?.attachments?.[0]) {
    throw new Error('Failed to upload attachment');
  }

  return response.data.data.attachments[0];
};

/**
 * Deletes an attachment
 * @param attachmentId Attachment ID to delete
 */
export const deleteAttachment = async (attachmentId: string): Promise<void> => {
  await apiClient.delete(`/attachments/${attachmentId}`);
};

/**
 * Gets download URL for an attachment
 * @param attachmentId Attachment ID
 * @returns Download URL
 */
export const getAttachmentUrl = (attachmentId: string): string => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5050/api';
  const token = tokenManager.getAccessToken();
  const url = `${baseUrl}/attachments/${attachmentId}/download`;
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

/**
 * Gets thumbnail URL for an attachment
 * @param attachmentId Attachment ID
 * @returns Thumbnail URL
 */
export const getAttachmentThumbnailUrl = (attachmentId: string): string => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5050/api';
  const token = tokenManager.getAccessToken();
  const url = `${baseUrl}/attachments/${attachmentId}/thumbnail`;
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

/**
 * Lists all attachments for a message
 * @param messageId Message ID
 * @returns Array of attachments
 */
export const listMessageAttachments = async (messageId: string): Promise<Attachment[]> => {
  const response = await apiClient.get<ApiResponse<{ attachments: Attachment[]; count: number }>>(
    `/messages/${messageId}/attachments`
  );

  if (!response.data.data?.attachments) {
    return [];
  }

  return response.data.data.attachments;
};

/**
 * Marks an attachment as permanent
 * @param attachmentId Attachment ID
 */
export const markAttachmentPermanent = async (attachmentId: string): Promise<void> => {
  await apiClient.put(`/attachments/${attachmentId}/permanent`);
};

/**
 * Uploads multiple files in a single request (backend supports max 10 files)
 * @param files Files to upload (max 10)
 * @param messageId Message ID to attach files to
 * @param onProgressUpdate Callback for overall progress
 * @returns Array of attachment metadata
 */
export const uploadMultipleFiles = async (
  files: File[],
  messageId: string,
  onProgressUpdate: (fileId: string, progress: number) => void
): Promise<Attachment[]> => {
  // Backend supports max 10 files per request
  if (files.length > 10) {
    throw new Error('Maximum 10 files allowed per upload');
  }

  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file); // All files in single request
  });

  const overallFileId = `batch-${Date.now()}`;

  const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0) || 1048576;

  const response = await apiClient.post<ApiResponse<{ attachments: Attachment[]; count: number }>>(
    `/messages/${messageId}/attachments`,
    formData,
    {
      timeout: 600000, // 10 minutes for large file uploads
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && progressEvent.total > 0) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          onProgressUpdate(overallFileId, Math.max(5, progress));
        } else if (progressEvent.loaded > 0) {
          const ratio = Math.min(progressEvent.loaded / totalSize, 0.95);
          onProgressUpdate(overallFileId, Math.max(5, Math.round(ratio * 95)));
        }
      },
    }
  );

  if (!response.data.data?.attachments) {
    throw new Error('Failed to upload attachments');
  }

  return response.data.data.attachments;
};

/**
 * Uploads a file with retry logic
 * @param file File to upload
 * @param messageId Message ID
 * @param onProgress Progress callback
 * @param maxRetries Maximum retry attempts (default: 3)
 * @param compress Whether to compress the file (for images/videos)
 * @returns Attachment metadata
 */
export const uploadWithRetry = async (
  file: File,
  messageId: string,
  onProgress?: (progress: number) => void,
  maxRetries = 3,
  compress?: boolean,
  displayType?: 'image' | 'file'
): Promise<Attachment> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await uploadAttachment(file, messageId, onProgress, compress, displayType);
    } catch (error) {
      lastError = error as Error;

      // Don't retry on validation errors (400)
      if (error instanceof Error && error.message.includes('400')) {
        throw error;
      }

      // Don't retry on auth errors (401/403)
      if (
        error instanceof Error &&
        (error.message.includes('401') || error.message.includes('403'))
      ) {
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Exponential backoff: wait 1s, 2s, 4s
      const delay = 1000 * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Exponential backoff retry
    }
  }

  throw lastError || new Error('Upload failed after retries');
};
